// components/RecordingStudio/VoiceBlock.jsx
import React from "react";
import {
  Play,
  Trash2,
  Loader,
  CheckCircle2,
  AlertCircle,
  Clock,
  Mic,
} from "lucide-react";

const formatDuration = (duration) => {
  if (!duration || duration <= 0) return null;
  const secs = duration > 300 ? duration / 1000 : duration;
  return secs < 10 ? `${secs.toFixed(1)}s` : `${Math.round(secs)}s`;
};

const resolveDisplayName = (block) =>
  block.voiceDisplayName || block.voiceName || "Voice";

// Animated waveform bars for playing state
const MiniWaveform = ({ active }) => (
  <div className="flex items-center gap-[2px]">
    {[4, 7, 5, 9, 6, 8, 4, 7, 5][0] &&
      [4, 7, 5, 9, 6, 8, 4, 7, 5].map((h, i) => (
        <div
          key={i}
          style={{
            height: active ? `${h}px` : "3px",
            animationDelay: `${i * 80}ms`,
            transition: "height 0.2s ease",
          }}
          className={`w-[2px] rounded-full ${active ? "bg-violet-400 animate-pulse" : "bg-white/20"}`}
        />
      ))}
  </div>
);

const STATUS_CONFIG = {
  completed: {
    icon: <CheckCircle2 size={10} className="text-emerald-400" />,
    dot: "bg-emerald-400",
    label: "ready",
  },
  processing: {
    icon: <Loader size={10} className="text-amber-400 animate-spin" />,
    dot: "bg-amber-400 animate-pulse",
    label: "synth",
  },
  failed: {
    icon: <AlertCircle size={10} className="text-red-400" />,
    dot: "bg-red-400",
    label: "error",
  },
  pending: {
    icon: <Clock size={10} className="text-gray-600" />,
    dot: "bg-gray-700",
    label: "draft",
  },
};

const AVATAR_GRADIENTS = [
  "from-violet-500 via-purple-500 to-fuchsia-500",
  "from-cyan-500 via-blue-500 to-indigo-500",
  "from-emerald-500 via-teal-500 to-cyan-500",
  "from-rose-500 via-pink-500 to-fuchsia-500",
  "from-amber-500 via-orange-500 to-rose-500",
  "from-lime-500 via-green-500 to-emerald-500",
];

const VoiceBlock = ({
  block,
  index,
  isSelected,
  onSelect,
  onSynthesize,
  onDelete,
  avatarImageUrl,
}) => {
  const displayName = resolveDisplayName(block);
  const durationLabel = formatDuration(block.duration);
  const isProcessing = block.status === "processing";
  const statusCfg = STATUS_CONFIG[block.status] || STATUS_CONFIG.pending;
  const gradient = AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length];

  return (
    <div
      onClick={onSelect}
      className={`relative group rounded-xl border cursor-pointer transition-all duration-200 overflow-hidden ${
        isSelected
          ? "border-violet-500/60 bg-gradient-to-br from-violet-500/10 to-fuchsia-500/5 shadow-lg shadow-violet-500/10"
          : "border-white/[0.06] hover:border-white/[0.12] bg-white/[0.02] hover:bg-white/[0.04]"
      }`}
    >
      {/* Selected glow line */}
      {isSelected && (
        <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-violet-400 to-fuchsia-500 rounded-r" />
      )}

      {/* Processing shimmer */}
      {isProcessing && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(167,139,250,0.04) 50%, transparent 100%)",
            animation: "shimmer 2s infinite",
          }}
        />
      )}

      <div className="px-3 py-2.5 pl-4">
        <div className="flex items-center gap-2.5">
          {/* Avatar */}
          <div
            className={`relative w-7 h-7 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center text-[11px] font-bold flex-shrink-0 overflow-hidden shadow-sm`}
          >
            {avatarImageUrl ? (
              <>
                <img
                  src={avatarImageUrl}
                  alt={displayName}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                <span
                  style={{ display: "none" }}
                  className="w-full h-full items-center justify-center"
                >
                  {displayName[0]}
                </span>
              </>
            ) : (
              <span>{displayName[0]}</span>
            )}
          </div>

          {/* Name */}
          <div className="flex-1 min-w-0">
            <span
              className={`text-[13px] font-semibold truncate block leading-none ${isSelected ? "text-white" : "text-gray-200"}`}
            >
              {displayName}
            </span>
          </div>

          {/* Status + duration */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {durationLabel && (
              <span className="text-[10px] text-gray-600 font-mono tabular-nums">
                {durationLabel}
              </span>
            )}
            <div
              className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusCfg.dot}`}
            />
          </div>

          {/* Hover actions */}
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ml-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSynthesize();
              }}
              disabled={isProcessing}
              className="w-6 h-6 flex items-center justify-center rounded-md text-gray-600 hover:text-violet-400 hover:bg-violet-500/10 disabled:opacity-30 transition-all"
            >
              <Play size={11} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm("Delete this block?")) onDelete();
              }}
              className="w-6 h-6 flex items-center justify-center rounded-md text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-all"
            >
              <Trash2 size={11} />
            </button>
          </div>
        </div>

        {/* Script preview + waveform */}
        <div className="flex items-center gap-2 mt-2 pl-9">
          {block.text ? (
            <p className="text-[11px] text-gray-600 line-clamp-1 flex-1 leading-none">
              {block.text}
            </p>
          ) : (
            <p className="text-[11px] text-gray-700 italic flex-1 leading-none">
              No script yet…
            </p>
          )}
          {block.status === "completed" && <MiniWaveform active={isSelected} />}
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
      `}</style>
    </div>
  );
};

export default VoiceBlock;
